/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  Directive,
  HostBinding,
  HostListener,
  Input,
  TemplateRef,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { nebulaSubscription } from '@app/core/ngrx/nebula-plans.selectors';
import { AppState } from '@app/core/types';
import { isSubscriptionUnpaid } from '@app/domain/nebula';
import { shareReplayLastWithRefCount } from '@common/rxjs-operators/share-replay-last-with-ref-count';
import { Store } from '@ngrx/store';
import { NebulaSubscriptionInfo } from '@shared/types/nebula';
import { Angulartics2 } from 'angulartics2';
import { Observable } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { MapPipe } from '../../pipes/map.pipe';

type CreateNebulaClusterButtonContext = {
  routerLink$: Observable<string | any[] | null | undefined>;
  disabled$: Observable<boolean>;
};

@Directive({
  selector: 'ng-template[createNebulaClusterButtonTemplate]',
  standalone: true,
})
export class CreateNebulaClusterButtonTemplateDirective {
  static ngTemplateContextGuard(
    dir: CreateNebulaClusterButtonTemplateDirective,
    ctx: unknown,
  ): ctx is CreateNebulaClusterButtonContext {
    return true;
  }
}

@Component({
  selector: 'create-nebula-cluster-button',
  templateUrl: 'create-nebula-cluster-button.component.html',
  styleUrls: ['./create-nebula-cluster-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [MatTooltipModule, NgTemplateOutlet, MatButtonModule, RouterLink, AsyncPipe, MapPipe],
})
export class CreateNebulaClusterButtonComponent {
  @Input()
  color: 'primary' | 'dark' = 'primary';

  @Input()
  text: 'Create cluster' | 'Create' = 'Create cluster';

  @ContentChild(CreateNebulaClusterButtonTemplateDirective, { read: TemplateRef })
  template?: TemplateRef<CreateNebulaClusterButtonContext>;

  noEmailTooltip = 'Please add your email to profile to be able to create a cluster';
  unpaidTooltip = 'Please resolve payment issues to create new clusters';

  nebulaSubscription$ = this.store.select(nebulaSubscription);

  disabled$: Observable<boolean> = this.nebulaSubscription$.pipe(
    filter(Boolean),
    map((subscription) => isSubscriptionUnpaid(subscription)),
    startWith(true),
    shareReplayLastWithRefCount,
  );

  // For some reason, disabling the button does not always disable the link
  public routerLink$ = this.disabled$.pipe(
    map((disabled) => (disabled ? null : ['/', { outlets: { dialog: ['provision'] } }])),
  );

  @HostBinding('class.dark')
  get isDark(): boolean {
    return this.color === 'dark';
  }

  constructor(
    private store: Store<AppState>,
    private angulartics2: Angulartics2,
  ) {}

  isUnpaid(subscription: NebulaSubscriptionInfo | null): boolean {
    return isSubscriptionUnpaid(subscription);
  }

  @HostListener('click', ['$event'])
  onClick(event: PointerEvent) {
    this.angulartics2.eventTrack.next({
      action: 'CreateCluster',
      properties: {
        category: 'ButtonClick',
        eventType: 'click',
      },
    });
  }
}
